import { z } from 'zod';
import { CANDIDATE_COLUMNS } from '../constants/columns';
import { FILL_COLORS } from '../constants/fill-colors';

/**
 * Линии сетки в таблице.
 *
 * Заказчик сказал: «границы в ячейках должны быть более яркие». Линии и
 * правда были бледнее некуда — горизонтали строк рисовались slate-100,
 * причём мимо токенов темы, так что в тёмной теме они были попросту
 * неверного цвета.
 *
 * Настройка общая для всех: таблица одна, и разный вид сетки у разных
 * людей мешал бы разговору «посмотри третью строку сверху».
 *
 * Почему цвет хранится парой, отдельно для светлой и тёмной темы: один
 * цвет на обе не работает. Всё, что заметно на белом, на тёмном фоне
 * либо исчезает, либо превращается в решётку.
 */

/** Цвет как его понимает CSS. Пресеты дают #RRGGBB, но поле не привязано к формату. */
export interface GridLineColors {
  light: string;
  dark: string;
}

export interface GridLinePreset extends GridLineColors {
  key: GridLinePresetKey;
  label: string;
}

export const GRID_LINE_PRESET_KEYS = ['subtle', 'normal', 'bright', 'contrast'] as const;
export type GridLinePresetKey = (typeof GRID_LINE_PRESET_KEYS)[number];

/**
 * Оттенки взяты из шкалы slate — той же, из которой собраны рамки и текст
 * остального интерфейса. Шаг между соседними пресетами — одна ступень
 * шкалы: меньше разницы человек на линии толщиной в пиксель не увидит,
 * и выбор превратится в четыре одинаковых плитки.
 */
export const GRID_LINE_PRESETS: readonly GridLinePreset[] = [
  { key: 'subtle', label: 'Едва заметные', light: '#e2e8f0', dark: '#1e293b' },
  { key: 'normal', label: 'Обычные', light: '#cbd5e1', dark: '#334155' },
  { key: 'bright', label: 'Яркие', light: '#94a3b8', dark: '#475569' },
  { key: 'contrast', label: 'Контрастные', light: '#64748b', dark: '#64748b' },
];

/** Толщина линии в пикселях. Ноль — линий этого направления нет вовсе. */
export const GRID_LINE_WIDTHS = [0, 1, 2] as const;
export type GridLineWidth = (typeof GRID_LINE_WIDTHS)[number];

const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Цвет задаётся шестью шестнадцатеричными знаками: #RRGGBB');

const widthSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);

export const gridLineSettingsSchema = z.object({
  /** Готовый набор или 'custom' — тогда цвет берётся из customLight/customDark */
  preset: z.enum([...GRID_LINE_PRESET_KEYS, 'custom']).default('normal'),
  customLight: hexColorSchema.default('#cbd5e1'),
  customDark: hexColorSchema.default('#334155'),
  /** Горизонтали — линии между строками */
  widthX: widthSchema.default(1),
  /** Вертикали — линии между колонками */
  widthY: widthSchema.default(1),
  /** Отдельная черта между строками, добавляемая через меню строки */
  separatorHeight: z.number().int().min(1).max(12).default(3),
  separatorColorLight: hexColorSchema.default('#77808d'),
  separatorColorDark: hexColorSchema.default('#89929f'),
});

export type GridLineSettings = z.infer<typeof gridLineSettingsSchema>;

/**
 * Цвет текста гражданства.
 *
 * Заказчик попросил: «РФ РБ гр-во колонку — должна быть возможность
 * по умолчанию выставить цвет ячейки», и отдельно — «вынеси в настройки».
 *
 * Цвета встроенных гражданств сохраняются независимо от справочника стран.
 * Сохранённые оттенки сохраняются при смене оформления: они задают текст,
 * а необязательная подложка использует тот же цвет с прозрачностью.
 *
 * `null` — «без цвета»: ячейка выглядит как любая другая. У «Иного»
 * так и есть по умолчанию — цветом различают РБ, РФ, РК и КР.
 */
export const citizenshipColorsSchema = z.object({
  BY: hexColorSchema.nullable().default('#d9ead3'),
  RU: hexColorSchema.nullable().default('#cfe2f3'),
  KZ: hexColorSchema.nullable().default('#fff2cc'),
  KG: hexColorSchema.nullable().default('#f4cccc'),
  OTHER: hexColorSchema.nullable().default(null),
});

export type CitizenshipColors = z.infer<typeof citizenshipColorsSchema>;

/** Общий вид подписей гражданства; выбранные для стран цвета хранятся отдельно. */
export const citizenshipStyleSchema = z.object({
  fill: z.boolean().default(false),
  weight: z.union([z.literal(400), z.literal(600), z.literal(700), z.literal(800)]).default(700),
});

export type CitizenshipStyle = z.infer<typeof citizenshipStyleSchema>;

/**
 * Общая палитра выбора цвета: справочники, ячейки, строки и оформление.
 *
 * Была прибита в коде, и это выяснилось ровно тогда, когда понадобилось
 * перенести цвета рабочей таблицы: часть оттенков в наборе была, часть
 * нет, и добавить их было нечем. Теперь набор — настройка.
 *
 * Подпись хранится вместе с цветом не для красоты: в таблице цвет заменял
 * слово («водителя нет», «едут вместе»), и без подписи это знание живёт
 * только в голове у того, кто красил.
 *
 * Предел в 24 цвета — не вкусовой: палитра рисуется одной полосой в меню
 * ячейки, и с четвёртого десятка выбирать в ней становится дольше, чем
 * взять системный выбор цвета, который стоит рядом.
 */
export const fillColorSchema = z.object({
  hex: hexColorSchema,
  label: z.string().trim().min(1, 'Подпишите цвет').max(40),
});

export const fillColorsSchema = z.array(fillColorSchema).min(1).max(24).default([...FILL_COLORS]);

export type FillColorSetting = z.infer<typeof fillColorSchema>;

/**
 * Насыщенность шрифта в заголовках колонок.
 *
 * Заказчик сказал: «Шрифт более жирный должен быть в колонках заголовка,
 * лучше сделать настраиваемо в настройках». Заголовки и правда были той же
 * насыщенности, что подписи в ячейках-ярлыках, — шапка не отделялась от
 * данных ничем, кроме фона.
 *
 * Умолчание поэтому не 600, как было, а 700: настройка без смены умолчания
 * означала бы, что заказчик открывает программу и не видит разницы.
 *
 * Шкала — 400, 600, 700, 800, и в ней нет 500. Это не вкус, а замер
 * в браузере: Inter из `--font-sans` установлен не везде, и на Windows
 * интерфейс рисуется вторым шрифтом набора — Segoe UI. У него 500 и 600
 * дают одно и то же начертание, буква в букву. Ступень «Обычный» на 500
 * означала бы пункт, который нажимается и ничего не меняет, — а такое
 * заказчик читает как поломку, и правильно делает.
 *
 * Настройка общая, как и линии сетки: таблица одна на всех.
 */
export const HEADER_FONT_WEIGHTS = [400, 600, 700, 800] as const;
export type HeaderFontWeight = (typeof HEADER_FONT_WEIGHTS)[number];

export const HEADER_FONT_WEIGHT_LABELS: Record<HeaderFontWeight, string> = {
  400: 'Обычный',
  600: 'Полужирный',
  700: 'Жирный',
  800: 'Очень жирный',
};

export const columnHeaderSettingsSchema = z.object({
  weight: z
    .union([z.literal(400), z.literal(600), z.literal(700), z.literal(800)])
    .default(700),
});

export type ColumnHeaderSettings = z.infer<typeof columnHeaderSettingsSchema>;

/**
 * Выравнивание ячеек основной таблицы — по колонкам.
 *
 * Заказчик: «В таблице 2026г нужно сделать выравнивание ячеек и вынести
 * настройку», и отдельно, отдельной строкой с пометкой «Важно»: делаем
 * по колонкам. Единица настройки — колонка, а не ячейка: заливка
 * ставится на каждую ячейку, выравнивание — на всю колонку сразу.
 *
 * До этого выравнивание было прибито в коде: пятнадцать колонок из сорока
 * одной несут `align` в `constants/columns.ts` — суммы справа, статусы
 * и даты по центру, — и поменять это можно было только релизом.
 *
 * Что записано, то и видно: колонки нет в мешке — она стоит так, как
 * стоит в коде. Поэтому настройка, которую никто не трогал, ничего
 * не двигает: суммы остаются столбиком справа. Общего значения «на всю
 * таблицу» здесь нет намеренно — оно было бы вторым слоем поверх кода,
 * и на вопрос «почему эта колонка так стоит» пришлось бы отвечать двумя
 * источниками вместо одного. «Поставить всем» на экране настроек просто
 * пишет значение каждой колонке.
 *
 * Вертикаль появилась потому, что её не было вовсе: ячейка стояла
 * посередине, а при включённом переносе текста — всегда сверху, и выбора
 * не было ни там, ни там. Теперь перенос отвечает только за перенос,
 * а умолчание вертикали — «посередине», то есть как было.
 *
 * Листов это не касается. У них своя цепочка `рука → колонка`
 * (`resolveCellView`), на ней держатся перенесённые из книги листы,
 * и общая настройка перекрасила бы их задним числом.
 */
export const CELL_ALIGN_X = ['left', 'center', 'right'] as const;
export type CellAlignX = (typeof CELL_ALIGN_X)[number];

export const CELL_ALIGN_Y = ['top', 'middle', 'bottom'] as const;
export type CellAlignY = (typeof CELL_ALIGN_Y)[number];

export const CELL_ALIGN_X_LABELS: Record<CellAlignX, string> = {
  left: 'По левому краю',
  center: 'По центру',
  right: 'По правому краю',
};

export const CELL_ALIGN_Y_LABELS: Record<CellAlignY, string> = {
  top: 'По верхнему краю',
  middle: 'Посередине',
  bottom: 'По нижнему краю',
};

/** Как ячейки стояли до этой настройки: перенос по словам прижимал их вверх */
export const FALLBACK_CELL_ALIGN_X: CellAlignX = 'left';
export const FALLBACK_CELL_ALIGN_Y: CellAlignY = 'middle';

/**
 * Выравнивание одной колонки. Обе половины необязательны: человек мог
 * подвинуть только по горизонтали, и вертикаль тогда остаётся заводской.
 */
export const columnAlignSchema = z.object({
  x: z.enum(CELL_ALIGN_X).optional(),
  y: z.enum(CELL_ALIGN_Y).optional(),
});

export type ColumnAlign = z.infer<typeof columnAlignSchema>;

export const cellAlignSettingsSchema = z.object({
  /**
   * Заголовок колонки встаёт так же, как её значения.
   *
   * Одной галкой на всю таблицу, а не признаком у каждой колонки:
   * подпись в шапке делит место с кнопкой меню и ручкой ширины, и на
   * узких колонках («№», «Пр.») она и без того обрывается многоточием.
   * Это свойство шапки целиком, а не отдельной колонки: сорок одна
   * галка отвечала бы на вопрос, которого никто не задавал.
   */
  header: z.boolean().default(true),
  /**
   * Ключ колонки → её выравнивание. Колонки здесь нет — значит, стоит
   * как в коде.
   *
   * Ключи не проверяются по списку колонок нарочно. Набор колонок
   * меняется релизом (Р-02), а настройка лежит в базе и переживает
   * релизы: строгая проверка означала бы, что после переименования
   * колонки весь раздел перестаёт разбираться и человек теряет заодно
   * сорок настроенных. Ключ от снятой колонки просто лежит и никем
   * не читается — правила собираются обходом колонок, а не мешка.
   */
  columns: z.record(z.string().max(64), columnAlignSchema).default({}),
});

export type CellAlignSettings = z.infer<typeof cellAlignSettingsSchema>;

export const appearanceSettingsSchema = z.object({
  gridLines: gridLineSettingsSchema.default(() => gridLineSettingsSchema.parse({})),
  citizenshipColors: citizenshipColorsSchema.default(() => citizenshipColorsSchema.parse({})),
  citizenshipStyle: citizenshipStyleSchema.default(() => citizenshipStyleSchema.parse({})),
  fillColors: fillColorsSchema,
  /** До первого сохранения администратора сервер берёт частые цвета справочников. */
  fillColorsConfigured: z.boolean().default(false),
  columnHeader: columnHeaderSettingsSchema.default(() => columnHeaderSettingsSchema.parse({})),
  cellAlign: cellAlignSettingsSchema.default(() => cellAlignSettingsSchema.parse({})),
});

export type AppearanceSettings = z.infer<typeof appearanceSettingsSchema>;

export const DEFAULT_GRID_LINES: GridLineSettings = gridLineSettingsSchema.parse({});

export const DEFAULT_CITIZENSHIP_COLORS: CitizenshipColors = citizenshipColorsSchema.parse({});

export const DEFAULT_CITIZENSHIP_STYLE: CitizenshipStyle = citizenshipStyleSchema.parse({});

export const DEFAULT_FILL_COLORS: FillColorSetting[] = [...FILL_COLORS];

export const DEFAULT_COLUMN_HEADER: ColumnHeaderSettings = columnHeaderSettingsSchema.parse({});

export const DEFAULT_CELL_ALIGN: CellAlignSettings = cellAlignSettingsSchema.parse({});

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  gridLines: DEFAULT_GRID_LINES,
  citizenshipColors: DEFAULT_CITIZENSHIP_COLORS,
  citizenshipStyle: DEFAULT_CITIZENSHIP_STYLE,
  fillColors: DEFAULT_FILL_COLORS,
  fillColorsConfigured: false,
  columnHeader: DEFAULT_COLUMN_HEADER,
  cellAlign: DEFAULT_CELL_ALIGN,
};

/**
 * Пара цветов, которой в итоге красятся линии.
 *
 * Отдаёт обе темы сразу, а не цвет под текущую: переключение темы не
 * должно ходить на сервер, обе переменные объявляются заранее и живут
 * в своих блоках `:root` и `.dark`.
 */
export function resolveGridLineColors(settings: GridLineSettings): GridLineColors {
  if (settings.preset === 'custom') {
    return { light: settings.customLight, dark: settings.customDark };
  }
  const preset = GRID_LINE_PRESETS.find((p) => p.key === settings.preset);
  // Пресет из будущей версии, которого здесь ещё нет, не должен оставлять
  // таблицу вообще без линий
  if (!preset) return { light: DEFAULT_GRID_LINES.customLight, dark: DEFAULT_GRID_LINES.customDark };
  return { light: preset.light, dark: preset.dark };
}

/** Значения CSS-переменных под настройку — одинаково у стора и у inline-скрипта загрузки. */
export function gridLineCssVars(settings: GridLineSettings): Record<string, string> {
  const colors = resolveGridLineColors(settings);
  return {
    '--grid-line-light': colors.light,
    '--grid-line-dark': colors.dark,
    '--grid-line-x-width': `${settings.widthX}px`,
    '--grid-line-y-width': `${settings.widthY}px`,
    '--grid-separator-height': `${settings.separatorHeight}px`,
    '--grid-separator-light': settings.separatorColorLight,
    '--grid-separator-dark': settings.separatorColorDark,
  };
}

/**
 * Переменная насыщенности заголовков.
 *
 * Имя нарочно не начинается с `--grid-line`: `.grid-line-header` — это
 * нижняя граница шапки, к шрифту отношения не имеющая, и одинаковая
 * приставка склеила бы в голове две разные вещи.
 */
export function columnHeaderCssVars(settings: ColumnHeaderSettings): Record<string, string> {
  return { '--grid-header-font-weight': String(settings.weight) };
}

/**
 * Выравнивание — переменными, а не классами на ячейке.
 *
 * Классы пришлось бы считать в `cellClass` на каждую ячейку, а их на
 * экране под восемьсот, и пересчёт идёт на каждой прокрутке. Переменная
 * достаётся ячейке от правила по её колонке, и дальше браузер применяет
 * её сам — во Vue не пересчитывается ничего.
 *
 * Значения сразу в терминах CSS (`flex-start`, а не `left`): иначе
 * перевод пришлось бы держать вторым экземпляром в таблице стилей,
 * где его не видно ни схеме, ни тестам.
 */
const CELL_JUSTIFY: Record<CellAlignX, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

const CELL_ITEMS: Record<CellAlignY, string> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
};

/**
 * Как в итоге стоит колонка: своё выравнивание, иначе то, что в коде.
 *
 * Горизонталь берётся из `constants/columns.ts` — там она у пятнадцати
 * колонок и есть тот вид, который человек видит сегодня. Вертикали в коде
 * нет ни у одной, поэтому её умолчание общее.
 */
export function resolveColumnAlign(
  settings: CellAlignSettings,
  column: { key: string; align?: CellAlignX },
): { x: CellAlignX; y: CellAlignY } {
  const own = settings.columns[column.key];
  return {
    x: own?.x ?? column.align ?? FALLBACK_CELL_ALIGN_X,
    y: own?.y ?? FALLBACK_CELL_ALIGN_Y,
  };
}

/**
 * Переменные одной колонки. Ими же красится образец на экране настроек —
 * поэтому образец показывает ровно то, что получится, а не похожее.
 *
 * `headerAlign` отдельным доводом, а не полем: у шапки своя галка на всю
 * таблицу, и колонка про неё ничего не знает. `null` — «шапка стоит
 * влево», то есть галка снята.
 */
export function columnAlignCssVars(
  align: { x: CellAlignX; y: CellAlignY },
  headerAlign: CellAlignX | null,
): Record<string, string> {
  return {
    '--grid-cell-justify': CELL_JUSTIFY[align.x],
    '--grid-cell-items': CELL_ITEMS[align.y],
    // Шапка выровнена по горизонтали и только: подпись в ней одна строка,
    // и вертикали у неё попросту нет
    '--grid-header-text-align': headerAlign ?? 'left',
  };
}

/**
 * Ключ, которым колонку можно назвать в селекторе.
 *
 * Здесь собирается текст таблицы стилей, а в мешок настроек ключи попали
 * из базы — то есть могли пережить и переименование колонки, и правку
 * руками. Ключ с кавычкой сломал бы не своё правило, а весь блок целиком,
 * и выравнивание пропало бы во всей таблице сразу. Проверка та же, что
 * у ключей колонок листа.
 */
const SAFE_KEY = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * Таблица стилей выравнивания: по правилу на колонку.
 *
 * Правило ставит колонке переменные, а `justify-content` и `align-items`
 * читают их в `.grid-cell-align` — по одному объявлению на всю таблицу.
 * Так выравнивание меняется без пересчёта ячеек: сменилась настройка —
 * пересобрался один блок текста.
 *
 * Правила пишутся всем колонкам, а не только настроенным. Писать
 * «отличающимся от умолчания» значило бы держать умолчание в двух
 * местах — здесь и в `style.css`, — и однажды они разойдутся.
 */
export function cellAlignCss(
  settings: CellAlignSettings,
  columns: readonly { key: string; align?: CellAlignX }[] = CANDIDATE_COLUMNS,
): string {
  const lines: string[] = [];
  for (const column of columns) {
    if (!SAFE_KEY.test(column.key)) continue;
    const align = resolveColumnAlign(settings, column);
    lines.push(
      `[data-col="${column.key}"]{--grid-cell-justify:${CELL_JUSTIFY[align.x]};` +
        `--grid-cell-items:${CELL_ITEMS[align.y]}}`,
    );
    // Галка снята — правил для шапки нет вовсе, и она стоит влево
    // по умолчанию из style.css. Так же, как было до настройки
    if (settings.header) {
      lines.push(`[data-header-key="${column.key}"]{--grid-header-text-align:${align.x}}`);
    }
  }
  return lines.join('\n');
}

/**
 * Все переменные вида одним набором.
 *
 * Собираются вместе, а не по разделу на вызов, ровно потому, что в кэше
 * они лежат одним ключом и ставятся одним проходом. Разделив, легко
 * получить настройку, которая сохранилась, но до экрана не доехала
 * до перезагрузки страницы.
 *
 * Выравнивания здесь нет: оно у каждой колонки своё, и на корень его
 * одним значением не поставить. Оно уезжает таблицей стилей
 * (`cellAlignCss`), а на корне остаётся только умолчание из `style.css`.
 */
export function appearanceCssVars(settings: AppearanceSettings): Record<string, string> {
  return {
    ...gridLineCssVars(settings.gridLines),
    ...columnHeaderCssVars(settings.columnHeader),
  };
}
